import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import HearingOutlinedIcon from "@mui/icons-material/HearingOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
import NavigateNextOutlinedIcon from "@mui/icons-material/NavigateNextOutlined";
import MDTypography from "components/MDTypography";
import { FixedSizeList as VirtualizedList } from "react-window";
import { useMaterialUIController } from "context";

const ATTRIBUTES = {
  modes: [],
};

const DEFAULT_PAGE_SIZE = 48;
const COMPACT_PAGE_SIZE = 15;

const ListOuter = React.forwardRef(function ListOuter(props, ref) {
  return (
    <div
      ref={ref}
      {...props}
      className={`contact-no-scrollbar ${props.className || ""}`}
      style={{
        ...props.style,
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      {props.children}
    </div>
  );
});

function ContactPanel({
  onLoad,
  registerCallBacks,
  searchText,
  onContactAction,
  isCompactScreen = false,
  contacts = [],
  contactsLoading = false,
  onRefreshContacts,
}) {
  const listContainerRef = useRef(null);
  const [listHeight, setListHeight] = useState(320);
  const [listWidth, setListWidth] = useState(280);
  const [currentPage, setCurrentPage] = useState(0);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const isCompactPaginationView = isCompactScreen || listWidth < 440;
  const pageSize = isCompactPaginationView
    ? COMPACT_PAGE_SIZE
    : DEFAULT_PAGE_SIZE;

  useEffect(() => {
    if (typeof onLoad === "function") {
      onLoad(ATTRIBUTES);
    }

    if (typeof registerCallBacks === "function") {
      registerCallBacks({
        refreshContactList: () => onRefreshContacts?.(),
      });
    }
  }, [onLoad, onRefreshContacts, registerCallBacks]);

  useEffect(() => {
    const measureListSize = () => {
      if (listContainerRef.current) {
        const nextHeight = listContainerRef.current.clientHeight;
        const nextWidth = listContainerRef.current.clientWidth;

        if (nextHeight > 0) {
          setListHeight(nextHeight);
        }

        if (nextWidth > 0) {
          setListWidth(nextWidth);
        }
      }
    };

    measureListSize();

    let resizeObserver;
    if (listContainerRef.current && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(measureListSize);
      resizeObserver.observe(listContainerRef.current);
    }

    window.addEventListener("resize", measureListSize);

    return () => {
      window.removeEventListener("resize", measureListSize);
      resizeObserver?.disconnect();
    };
  }, [isCompactScreen]);

  const columnCount = useMemo(() => {
    if (isCompactScreen) {
      return 1;
    }

    if (listWidth >= 660) {
      return 4;
    }

    if (listWidth >= 440) {
      return 3;
    }

    return 1;
  }, [isCompactScreen, listWidth]);

  const filteredContacts = useMemo(() => {
    const query = (searchText || "").trim().toLowerCase();

    if (!query) {
      return contacts;
    }

    return contacts.filter((contact) => {
      const fields = [contact?.designation, contact?.name, contact?.offEXtn];
      return fields.some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(query),
      );
    });
  }, [contacts, searchText]);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchText, pageSize]);

  const totalPages = useMemo(
    () =>
      filteredContacts.length > 0
        ? Math.ceil(filteredContacts.length / pageSize)
        : 0,
    [filteredContacts.length, pageSize],
  );

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages - 1) {
      setCurrentPage(totalPages - 1);
    }

    if (totalPages === 0 && currentPage !== 0) {
      setCurrentPage(0);
    }
  }, [currentPage, totalPages]);

  const paginatedContacts = useMemo(() => {
    const start = currentPage * pageSize;
    return filteredContacts.slice(start, start + pageSize);
  }, [currentPage, filteredContacts, pageSize]);

  const rangeLabel = useMemo(() => {
    if (filteredContacts.length === 0) {
      return "Contacts: 0";
    }

    const start = currentPage * pageSize + 1;
    const end = Math.min(
      start + paginatedContacts.length - 1,
      filteredContacts.length,
    );

    return `Contacts: ${filteredContacts.length}`;
  }, [
    currentPage,
    filteredContacts.length,
    pageSize,
    paginatedContacts.length,
  ]);

  const canGoPrevious = currentPage > 0;
  const canGoNext = totalPages > 0 && currentPage < totalPages - 1;

  const renderContactRow = useCallback(
    ({ index, style, data }) => {
      const { contacts: rowContacts, columns } = data;
      const startIndex = index * columns;
      const contactsInRow = rowContacts.slice(startIndex, startIndex + columns);

      return (
        <Box style={style} sx={{ px: 0 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
              gap: 0.55,
            }}
          >
            {contactsInRow.map((contact, rowItemIndex) => {
              const name = contact?.designation || contact?.name || "Unknown";
              const number = contact?.offEXtn || "--";

              return (
                <Card
                  key={`${contact?.id || name}-${number}-${rowItemIndex}`}
                  sx={(theme) => ({
                    p: isCompactScreen ? 0.35 : 0.45,
                    borderRadius: 1.2,
                    height: "100%",
                    border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.info.main, 0.1)}`,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: `0 4px 14px ${alpha(theme.palette.info.main, 0.2)}`,
                      border: `1px solid ${alpha(theme.palette.info.main, 0.32)}`,
                    },
                  })}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.45,
                    }}
                  >
                    <Box sx={{ minWidth: 0, p: 1, display: "flex", flexDirection: "column", gap: 0.4 }}>
                      <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        noWrap
                        sx={{ fontSize: "0.68rem" }}
                      >
                        {name}
                      </MDTypography>
                      <Chip
                        label={number}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{
                          height: 16,
                          fontSize: "0.58rem",
                          fontWeight: 600,
                          alignSelf: "flex-start",
                          "& .MuiChip-label": { px: 0.7 },
                        }}
                      />
                    </Box>

                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.55 }}
                    >
                      <Tooltip title="Audio Call">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onContactAction?.("call", contact)}
                            sx={{
                              width: 28,
                              height: 28,
                              border: (theme) =>
                                `1px solid ${alpha(theme.palette.success.main, 0.38)}`,
                              backgroundColor: (theme) =>
                                alpha(theme.palette.success.main, 0.12),
                            }}
                          >
                            <CallOutlinedIcon
                              fontSize="small"
                              color="success"
                            />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Video Call">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() =>
                              onContactAction?.("video-call", contact)
                            }
                            sx={{
                              width: 28,
                              height: 28,
                              border: (theme) =>
                                `1px solid ${alpha(theme.palette.info.main, 0.38)}`,
                              backgroundColor: (theme) =>
                                alpha(theme.palette.info.main, 0.12),
                            }}
                          >
                            <VideocamOutlinedIcon
                              fontSize="small"
                              color="info"
                            />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Ambient Mode">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() =>
                              onContactAction?.("ambient-mode", contact)
                            }
                            sx={{
                              width: 28,
                              height: 28,
                              border: (theme) =>
                                `1px solid ${alpha(theme.palette.error.main, 0.38)}`,
                              backgroundColor: (theme) =>
                                alpha(theme.palette.error.main, 0.12),
                            }}
                          >
                            <HearingOutlinedIcon
                              fontSize="small"
                              color="error"
                            />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Message">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() =>
                              onContactAction?.("message", contact)
                            }
                            sx={{
                              width: 28,
                              height: 28,
                              border: (theme) =>
                                `1px solid ${alpha(theme.palette.warning.main, 0.38)}`,
                              backgroundColor: (theme) =>
                                alpha(theme.palette.warning.main, 0.12),
                            }}
                          >
                            <ChatOutlinedIcon
                              fontSize="small"
                              color="warning"
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                </Card>
              );
            })}
          </Box>
        </Box>
      );
    },
    [darkMode, isCompactScreen, onContactAction],
  );

  return (
    <Box
      sx={(theme) => ({
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
        borderRadius: 1.4,
        p: 0.6,
        // background: darkMode
        //     ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)"
        //     : "linear-gradient(135deg, #f5f5f5 0%, #e3f2fd 50%, #f0f4ff 100%)",
      })}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "nowrap",
          gap: 0.5,
          px: 0.4,
        }}
      >
        <Chip
          label={rangeLabel}
          size="small"
          color="info"
          variant="outlined"
          sx={{ height: 22, fontSize: "0.65rem", fontWeight: 600, px: 0.5 }}
        />
        <Tooltip title="Refresh contacts">
          <IconButton
            size="small"
            onClick={() => onRefreshContacts?.()}
            sx={{
              width: 26,
              height: 26,
              border: (theme) =>
                `1px solid ${alpha(theme.palette.info.main, 0.32)}`,
              borderRadius: "50%",
            }}
          >
            <RefreshOutlinedIcon fontSize="small" color="info" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        ref={listContainerRef}
        sx={(theme) => ({
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          borderRadius: 1.2,
          border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
          p: 0.5,
          // background:
          //   darkMode
          //     ? "linear-gradient(135deg, rgba(14,20,40,0.92) 0%, rgba(14,42,82,0.9) 100%)"
          //     : "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(236,246,255,0.92) 100%)",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            width: 0,
            height: 0,
            display: "none",
          },
          "& .contact-no-scrollbar::-webkit-scrollbar": {
            width: 0,
            height: 0,
            display: "none",
          },
        })}
      >
        {contactsLoading ? (
          <Box
            sx={{
              minHeight: 150,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <CircularProgress size={22} color="info" />
            <MDTypography variant="caption" color="text" sx={{ opacity: 0.74 }}>
              Loading contacts...
            </MDTypography>
          </Box>
        ) : filteredContacts.length === 0 ? (
          <Box
            sx={{
              minHeight: 140,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
              No contacts found.
            </MDTypography>
          </Box>
        ) : (
          <VirtualizedList
            height={listHeight}
            width="100%"
            itemSize={isCompactScreen ? 52 : columnCount === 3 ? 54 : 56}
            itemCount={Math.ceil(paginatedContacts.length / columnCount)}
            itemData={{ contacts: paginatedContacts, columns: columnCount }}
            outerElementType={ListOuter}
            itemKey={(index, data) => {
              const startIndex = index * data.columns;
              const firstContact = data.contacts[startIndex] || {};
              const name =
                firstContact?.designation || firstContact?.name || "unknown";
              const number =
                firstContact?.offEXtn || firstContact?.extension || index;

              return `${currentPage}-${firstContact?.id || name}-${number}`;
            }}
          >
            {renderContactRow}
          </VirtualizedList>
        )}
      </Box>

      {!contactsLoading && filteredContacts.length > 0 && (
        <Box>
          <Box
            sx={(theme) => ({
              height: "1px",
              mx: 0.5,
              mb: 0.5,
              background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.divider, 0.6)}, transparent)`,
            })}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 0.5,
              px: 0.4,
            }}
          >
            <MDTypography variant="caption" color="text" sx={{ opacity: 0.74, fontSize: "0.63rem" }}>
              Page {totalPages === 0 ? 0 : currentPage + 1} / {totalPages}
            </MDTypography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              <IconButton
                size="small"
                color="info"
                disabled={!canGoPrevious}
                onClick={() =>
                  setCurrentPage((previous) => Math.max(previous - 1, 0))
                }
                sx={{
                  width: 26,
                  height: 26,
                  border: (theme) =>
                    `1px solid ${alpha(theme.palette.info.main, canGoPrevious ? 0.45 : 0.18)}`,
                  borderRadius: "50%",
                  transition: "all 0.2s ease",
                  "&:hover:not(:disabled)": {
                    backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12),
                  },
                }}
              >
                <NavigateBeforeOutlinedIcon fontSize="small" />
              </IconButton>

              <IconButton
                size="small"
                color="info"
                disabled={!canGoNext}
                onClick={() =>
                  setCurrentPage((previous) =>
                    totalPages > 0
                      ? Math.min(previous + 1, totalPages - 1)
                      : previous,
                  )
                }
                sx={{
                  width: 26,
                  height: 26,
                  border: (theme) =>
                    `1px solid ${alpha(theme.palette.info.main, canGoNext ? 0.45 : 0.18)}`,
                  borderRadius: "50%",
                  transition: "all 0.2s ease",
                  "&:hover:not(:disabled)": {
                    backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12),
                  },
                }}
              >
                <NavigateNextOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default ContactPanel;
